import { useEffect, useRef, useState } from "react";
import api, { isVersionConflict } from "../../lib/api";
import {
  ACTIONS, ACTION_HELP, ADDABLE_ROLES, MIN_REASON, PRESENCE_LABELS, ROLE_LABELS, isoToLocalInput, localInputToIso, missionErrorMessage, periodError,
  warningKinds, warningsFrom,
} from "../../lib/missions";
import { MemberKindHint, MemberPicker, WarningsNotice } from "./MissionBits";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Loader2 } from "lucide-react";


// One submit path for every mission dialog: busy state, the server's warnings to confirm, a plain error, and a reload of the
// mission when it was changed meanwhile (409 version_conflict).
function useMissionRequest({ onDone, onConflict }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [warnings, setWarnings] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const reset = () => { setError(""); setWarnings(null); setConfirmed(false); };
  const send = async (request, success) => {
    setBusy(true);
    setError("");
    try {
      const { data } = await request(warnings && confirmed ? warningKinds(warnings) : []);
      reset();
      onDone(data, success);
    } catch (e) {
      const found = warningsFrom(e);
      if (found) { setWarnings(found); setConfirmed(false); return; }
      setError(missionErrorMessage(e));
      if (isVersionConflict(e)) onConflict?.();
    } finally {
      setBusy(false);
    }
  };
  const blocked = !!warnings && !confirmed;
  return { busy, error, warnings, confirmed, setConfirmed, reset, send, blocked };
}

function DialogError({ text }) {
  return text ? <p role="alert" className="text-sm text-red-700" data-testid="mission-dialog-error">{text}</p> : null;
}

// Plan / start / finish / postpone / interrupt / cancel.
export function MissionActionDialog({ mission, action, open, onOpenChange, onDone, onConflict, missingProcedures = [] }) {
  const meta = ACTIONS[action] || {};
  const [reason, setReason] = useState("");
  const [derogation, setDerogation] = useState("");
  const [missing, setMissing] = useState([]);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const req = useMissionRequest({ onDone, onConflict });

  useEffect(() => {
    if (!open || !mission) return;
    setReason("");
    setDerogation("");
    setMissing(action === "finish" ? missingProcedures : []);
    setStart(isoToLocalInput(mission.planned_start_at, mission.timezone));
    setEnd(isoToLocalInput(mission.planned_end_at, mission.timezone));
    req.reset();
  }, [open, action]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mission || !action) return null;
  const startIso = localInputToIso(start, mission.timezone);
  const endIso = localInputToIso(end, mission.timezone);
  const datesError = meta.dates ? periodError(startIso, endIso) : "";
  const reasonOk = !meta.reason || reason.trim().length >= MIN_REASON;
  // Finishing while a required procedure is open needs a reasoned derogation; the server checks it again and, if the list
  // changed meanwhile, its answer (procedures_incomplete) brings the up-to-date list here.
  const derogationOk = missing.length === 0 || derogation.trim().length >= MIN_REASON;
  const valid = reasonOk && derogationOk && !datesError && !req.blocked;

  const submit = () => req.send((confirm) => api.post(`/missions/${mission.id}/${action}`, {
    expected_version: mission.version,
    ...(meta.reason ? { reason: reason.trim() } : {}),
    ...(meta.dates ? { planned_start_at: startIso, planned_end_at: endIso } : {}),
    ...(confirm.length ? { confirm_warnings: confirm } : {}),
    ...(missing.length ? { derogation_reason: derogation.trim() } : {}),
  }).catch((e) => {
    const detail = e?.response?.data?.detail;
    if (detail?.code === "procedures_incomplete") setMissing(detail.missing || []);
    throw e;
  }), meta.done);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid={`mission-action-dialog-${action}`}>
        <DialogHeader>
          <DialogTitle>{meta.title} ?</DialogTitle>
          <DialogDescription>{ACTION_HELP[action]}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {meta.dates && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2"><Label htmlFor="postpone-start">Nouveau début *</Label>
                <Input id="postpone-start" type="datetime-local" className="text-base md:text-sm" value={start} onChange={(e) => setStart(e.target.value)} data-testid="mission-action-start" /></div>
              <div className="space-y-2"><Label htmlFor="postpone-end">Nouvelle fin *</Label>
                <Input id="postpone-end" type="datetime-local" className="text-base md:text-sm" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="mission-action-end" /></div>
              {datesError && <p className="text-sm text-amber-700 sm:col-span-2">{datesError}</p>}
            </div>
          )}
          {meta.reason && (
            <div className="space-y-2">
              <Label htmlFor="mission-action-reason">Motif *</Label>
              <Textarea id="mission-action-reason" rows={3} maxLength={300} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)}
                placeholder="Il restera dans l'historique de la mission." data-testid="mission-action-reason" />
            </div>
          )}
          {missing.length > 0 && (
            <div className="space-y-2" data-testid="mission-derogation">
              <p className="text-sm text-amber-800">
                Procédure{missing.length > 1 ? "s" : ""} obligatoire{missing.length > 1 ? "s" : ""} non terminée{missing.length > 1 ? "s" : ""} : {missing.join(", ")}.
                Indiquez pourquoi la mission se termine quand même : ce sera inscrit à son historique.
              </p>
              <Label htmlFor="mission-derogation-reason">Motif de la dérogation *</Label>
              <Textarea id="mission-derogation-reason" rows={3} maxLength={300} className="text-base md:text-sm" value={derogation}
                onChange={(e) => setDerogation(e.target.value)} data-testid="mission-derogation-reason" />
            </div>
          )}
          <WarningsNotice warnings={req.warnings} confirmed={req.confirmed} onConfirmedChange={req.setConfirmed} />
          <DialogError text={req.error} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Fermer</Button>
          <Button onClick={submit} disabled={!valid || req.busy} data-testid="mission-action-confirm"
            className={meta.danger ? "bg-red-600 hover:bg-red-700 text-white" : undefined}>
            {req.busy && <Loader2 size={14} className="animate-spin mr-1.5" />}{req.warnings && req.confirmed ? "Confirmer quand même" : meta.label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Title, objective and, while the mission is not yet on the agenda (draft / postponed), its dates.
export function MissionEditDialog({ mission, open, onOpenChange, onDone, onConflict }) {
  const [title, setTitle] = useState("");
  const [objective, setObjective] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const req = useMissionRequest({ onDone, onConflict });
  const datesEditable = mission && ["draft", "postponed"].includes(mission.status);

  useEffect(() => {
    if (!open || !mission) return;
    setTitle(mission.title);
    setObjective(mission.objective || "");
    setStart(isoToLocalInput(mission.planned_start_at, mission.timezone));
    setEnd(isoToLocalInput(mission.planned_end_at, mission.timezone));
    req.reset();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mission) return null;
  const startIso = localInputToIso(start, mission.timezone);
  const endIso = localInputToIso(end, mission.timezone);
  const datesError = datesEditable ? periodError(startIso, endIso) : "";
  const body = { expected_version: mission.version };
  if (title.trim() !== mission.title) body.title = title.trim();
  if ((objective.trim() || null) !== (mission.objective || null)) body.objective = objective.trim() || null;
  if (datesEditable && (startIso !== new Date(mission.planned_start_at).toISOString() || endIso !== new Date(mission.planned_end_at).toISOString())) {
    body.planned_start_at = startIso;
    body.planned_end_at = endIso;
  }
  const changed = Object.keys(body).length > 1;
  const valid = changed && title.trim().length > 0 && !datesError;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="mission-edit-dialog">
        <DialogHeader>
          <DialogTitle>Modifier la mission</DialogTitle>
          {!datesEditable && <DialogDescription>Les dates d'une mission planifiée se changent avec « Reporter », qui demande un motif.</DialogDescription>}
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2"><Label htmlFor="edit-title">Titre *</Label>
            <Input id="edit-title" maxLength={120} className="text-base md:text-sm" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="mission-edit-title" /></div>
          <div className="space-y-2"><Label htmlFor="edit-objective">Objectif</Label>
            <Textarea id="edit-objective" rows={3} maxLength={2000} className="text-base md:text-sm" value={objective} onChange={(e) => setObjective(e.target.value)} /></div>
          {datesEditable && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2"><Label htmlFor="edit-start">Début prévu</Label>
                <Input id="edit-start" type="datetime-local" className="text-base md:text-sm" value={start} onChange={(e) => setStart(e.target.value)} data-testid="mission-edit-start" /></div>
              <div className="space-y-2"><Label htmlFor="edit-end">Fin prévue</Label>
                <Input id="edit-end" type="datetime-local" className="text-base md:text-sm" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="mission-edit-end" /></div>
              {datesError && <p className="text-sm text-amber-700 sm:col-span-2">{datesError}</p>}
            </div>
          )}
          <DialogError text={req.error} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Annuler</Button>
          <Button onClick={() => req.send(() => api.patch(`/missions/${mission.id}`, body), "Mission mise à jour")} disabled={!valid || req.busy}
             data-testid="mission-edit-save">
            {req.busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddMemberDialog({ mission, open, onOpenChange, onDone, onConflict }) {
  const [member, setMember] = useState(null);
  const [role, setRole] = useState("member");
  const [presence, setPresence] = useState("full");
  const req = useMissionRequest({ onDone, onConflict });
  const hasLead = !!mission?.lead;
  const pickedKey = useRef("");

  useEffect(() => {
    if (!open) return;
    setMember(null);
    setRole(hasLead ? "member" : "lead");
    setPresence("full");
    req.reset();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // A different person or role is a new request: the warnings read for the previous one no longer apply.
  useEffect(() => {
    const key = `${member?.kind}:${member?.id}:${role}`;
    if (key !== pickedKey.current) { pickedKey.current = key; req.reset(); }
  }, [member, role]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mission) return null;
  const roles = hasLead ? ADDABLE_ROLES : ["lead", ...ADDABLE_ROLES];
  const exclude = mission.team.map((t) => `${t.member.kind}:${t.member.id}`);
  const submit = () => req.send((confirm) => api.post(`/missions/${mission.id}/assignments`, {
    expected_version: mission.version, member: { kind: member.kind, id: member.id }, role_in_mission: role, expected_presence: presence,
    ...(confirm.length ? { confirm_warnings: confirm } : {}),
  }), `${member.name} ajouté${member.kind === "user" ? "" : "(e)"} à l'équipe`);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="mission-add-member-dialog">
        <DialogHeader>
          <DialogTitle>Ajouter une personne à l'équipe</DialogTitle>
          <DialogDescription>Un compte CRM verra cette mission et le contact de la pharmacie. Un collaborateur sans compte n'a aucun accès.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Personne *</Label>
            <MemberPicker value={member} onChange={setMember} exclude={exclude} testid="mission-add-member-picker" />
            {member && <MemberKindHint kind={member.kind} />}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2"><Label htmlFor="member-role">Rôle dans la mission</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger id="member-role" data-testid="mission-add-member-role"><SelectValue /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-2"><Label htmlFor="member-presence">Présence attendue</Label>
              <Select value={presence} onValueChange={setPresence}>
                <SelectTrigger id="member-presence"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PRESENCE_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
          </div>
          <WarningsNotice warnings={req.warnings} confirmed={req.confirmed} onConfirmedChange={req.setConfirmed} />
          <DialogError text={req.error} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Annuler</Button>
          <Button onClick={submit} disabled={!member || req.blocked || req.busy}  data-testid="mission-add-member-confirm">
            {req.busy && <Loader2 size={14} className="animate-spin mr-1.5" />}{req.warnings && req.confirmed ? "Confirmer quand même" : "Ajouter"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RemoveMemberDialog({ mission, assignment, open, onOpenChange, onDone, onConflict }) {
  const [reason, setReason] = useState("");
  const req = useMissionRequest({ onDone, onConflict });
  useEffect(() => { if (open) { setReason(""); req.reset(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!mission || !assignment) return null;
  const valid = reason.trim().length >= MIN_REASON;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mission-remove-member-dialog">
        <DialogHeader>
          <DialogTitle>Retirer {assignment.member.name} de l'équipe ?</DialogTitle>
          <DialogDescription>
            {assignment.member.kind === "user" ? "Cette personne ne verra plus la mission. " : ""}Son affectation reste visible dans l'équipe (« retirés ») et dans l'historique.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="remove-reason">Motif *</Label>
          <Textarea id="remove-reason" rows={2} maxLength={300} className="text-base md:text-sm" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="mission-remove-reason" />
          <DialogError text={req.error} />
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Annuler</Button>
          <Button onClick={() => req.send(() => api.post(`/missions/${mission.id}/assignments/${assignment.id}/remove`, { expected_version: mission.version, reason: reason.trim() }),
            `${assignment.member.name} retiré(e) de l'équipe`)} disabled={!valid || req.busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="mission-remove-confirm">
            {req.busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Retirer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function LeadDialog({ mission, assignment, open, onOpenChange, onDone, onConflict }) {
  const req = useMissionRequest({ onDone, onConflict });
  useEffect(() => { if (open) req.reset(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!mission || !assignment) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="mission-lead-dialog">
        <DialogHeader>
          <DialogTitle>Confier la responsabilité à {assignment.member.name} ?</DialogTitle>
          <DialogDescription>{mission.lead ? `${mission.lead.name} reste dans l'équipe comme intervenant.` : "Cette personne coordonnera la mission."}</DialogDescription>
        </DialogHeader>
        <DialogError text={req.error} />
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={req.busy}>Annuler</Button>
          <Button onClick={() => req.send(() => api.post(`/missions/${mission.id}/lead`, { expected_version: mission.version, assignment_id: assignment.id }),
            `${assignment.member.name} est responsable de la mission`)} disabled={req.busy}  data-testid="mission-lead-confirm">
            {req.busy && <Loader2 size={14} className="animate-spin mr-1.5" />} Confirmer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
